export type RiskEvidenceProof = {
  available: boolean;
  eventProofReady?: boolean;
  statisticsReady?: boolean;
  proofReady: boolean;
  ticker?: string | null;
  minimumSample: number;
  qualifyingEventCount: number;
  completed60mSampleCount: number;
  remainingToStatistics?: number;
  uniqueTickerCount?: number;
  uniqueSessionCount?: number;
  progressPct: number;
  periodStart?: string | null;
  periodEnd?: string | null;
  actionPolicyVersions?: string[];
  riskCalculationVersions?: string[];
  evidenceVersions?: string[];
  evidenceVersion?: string;
  windowDays?: number;
  sample10m?: number;
  avgReturn10mPct?: number | null;
  sample30m?: number;
  avgReturn30mPct?: number | null;
  sample60m?: number;
  avgReturn60mPct?: number | null;
  declineRate60mPct?: number | null;
  sampleClose?: number;
  avgReturnClosePct?: number | null;
  sampleNextOpen?: number;
  avgReturnNextOpenPct?: number | null;
  sampleNextClose?: number;
  avgReturnNextClosePct?: number | null;
  sampleExcursion?: number;
  avgMaePct?: number | null;
  avgMfePct?: number | null;
  byState?: Array<{
    riskState: string;
    qualifyingEventCount: number;
    completed60mSampleCount: number;
    eventProofReady?: boolean;
    statisticsReady?: boolean;
    proofReady: boolean;
    avgReturn60mPct?: number | null;
    declineRate60mPct?: number | null;
    avgMaePct?: number | null;
  }>;
  recentEvents?: RiskEvidenceRecentItem[];
  methodology?: string[];
  reason?: string;
};

export type RiskEvidenceRecentItem = {
  id?: number;
  ticker?: string;
  corpName?: string;
  marketType?: string;
  sessionDate?: string;
  detectedAt?: string;
  captureType?: string;
  episodeNo?: number;
  riskState?: string;
  riskStateLabel?: string;
  riskSummary?: string;
  riskScore?: number | null;
  riskLevel?: string;
  actionCode?: string;
  actionHeadline?: string;
  eventPrice?: number | null;
  outcome10mReturnPct?: number | null;
  outcome10mStatus?: string;
  outcome30mReturnPct?: number | null;
  outcome30mStatus?: string;
  outcome60mReturnPct?: number | null;
  outcome60mStatus?: string;
  closeReturnPct?: number | null;
  closeStatus?: string;
  nextOpenReturnPct?: number | null;
  nextOpenStatus?: string;
  nextCloseReturnPct?: number | null;
  nextCloseStatus?: string;
  mfePct?: number | null;
  maePct?: number | null;
  excursionStatus?: string;
  resolvedAt?: string;
  resolvedRiskState?: string;
  actionPolicyVersion?: string;
  riskCalculationVersion?: string;
  payloadHashPrefix?: string;
};

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {Accept: 'application/json'},
    signal,
  });
  if (!response.ok) {
    throw new Error(`Risk evidence request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export function getRiskEvidenceSummary(signal?: AbortSignal): Promise<RiskEvidenceProof> {
  return getJson<RiskEvidenceProof>('/api/live-summary/risk-evidence/summary', signal);
}

export function getTickerRiskEvidence(
    ticker: string,
    signal?: AbortSignal,
): Promise<RiskEvidenceProof> {
  const normalized = String(ticker ?? '').replace(/[^0-9]/g, '').slice(-6);
  return getJson<RiskEvidenceProof>(
      `/api/live-summary/risk-evidence/stocks/${encodeURIComponent(normalized)}`,
      signal,
  );
}

export function getRecentRiskEvidence(
    limit = 20,
    signal?: AbortSignal,
): Promise<RiskEvidenceRecentItem[]> {
  const safeLimit = Math.max(1, Math.min(100, Math.round(limit)));
  return getJson<RiskEvidenceRecentItem[]>(
      `/api/live-summary/risk-evidence/recent?limit=${safeLimit}`,
      signal,
  );
}

export type RiskEvidenceChainVerification = {
  ticker: string;
  evidenceVersion?: string;
  available: boolean;
  verified: boolean;
  checkedRows: number;
  firstBrokenId?: number | null;
  latestHashPrefix?: string;
  note?: string;
  reason?: string;
};

export function getTickerRiskEvidenceVerification(
    ticker: string,
    signal?: AbortSignal,
): Promise<RiskEvidenceChainVerification> {
  const normalized = String(ticker ?? '').replace(/[^0-9]/g, '').slice(-6);
  return getJson<RiskEvidenceChainVerification>(
      `/api/live-summary/risk-evidence/verify/${encodeURIComponent(normalized)}`,
      signal,
  );
}
